import { LegalDocument, LegalDocumentsMetadata } from "@domain/legal-content/legal-content";
import { LegalContentService } from "@domain/legal-content/legal-content-service";
import { Store } from "@core/storage/storage";
import { observable } from "micro-observables";
import { getLogger } from "@core/logging/logger";
import { InfraManager } from "@domain/infra/infra-manager";

export class LegalContentManager {
	private readonly logger = getLogger("📜 [LegalContentManager]");

	private readonly _termsOfUse = observable<LegalDocument | null>(null);
	termsOfUse = this._termsOfUse.readOnly();

	private readonly _privacyNotice = observable<LegalDocument | null>(null);
	privacyNotice = this._privacyNotice.readOnly();

	private readonly _legalDocumentsMetadata = observable<LegalDocumentsMetadata | null>(null);
	legalDocumentsMetadata = this._legalDocumentsMetadata.readOnly();

	constructor(
		private readonly legalContentService: LegalContentService,
		private readonly infraManager: InfraManager,
		private readonly store: Store<LegalDocumentsMetadata>,
	) {
		this.infraManager.targetInfraChanged.add(this.retrieveDocumentsMetadata.bind(this));
	}

	async retrieveLegalDocuments(locale: string): Promise<void> {
		await Promise.all([this.retrieveTermsOfUse(locale), this.retrievePrivacyNotice(locale)]);
	}

	async retrieveTermsOfUse(locale: string): Promise<void> {
		this._termsOfUse.set(await this.legalContentService.getTermsOfUse(locale));
	}

	async retrievePrivacyNotice(locale: string): Promise<void> {
		this._privacyNotice.set(await this.legalContentService.getPrivacyNotice(locale));
	}

	async retrieveDocumentsMetadata(): Promise<void> {
		let documents: LegalDocumentsMetadata | undefined;

		try {
			documents = await this.legalContentService.getLegalDocumentsMetadata();
			this.store.setItem(documents);
		} catch (e) {
			documents = this.store.getItem();
		}

		this._legalDocumentsMetadata.set(documents);
	}

	async consentToLegalDocuments(): Promise<void> {
		const termsOfUse = this._termsOfUse.get();
		const privacyNotice = this._privacyNotice.get();
		if (termsOfUse && privacyNotice) {
			await this.legalContentService.consentToLegalDocuments(termsOfUse.version, privacyNotice.version);
		} else {
			throw new Error("Impossible to consent because either the privacy or terms of use are missing");
		}
	}
}
